import { useState, useRef, useEffect } from "react";
import { bucket } from "../../../firebase/config";
import { v4 as uuid } from "uuid";
import useInterviewProcessor from "../useInterviewProcessor";
import ProgressBar from "../ProgressBar";
import PrivacyStepsProgress from "../PrivacyStepsProgress";
import TranscriptReview from "../TranscriptReview";

const AudioRecorderTab = ({ interview, onPhaseChange, onAnalysisComplete, onAnalysisError }) => {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const { privacySteps, phase, error, transcript, setTranscript, runPrivacy, save } =
    useInterviewProcessor(interview);

  // Let the detail page know which phase we're in, so it can hide the
  // "Audio opnemen" heading and tab buttons once processing starts.
  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  const startRecording = async () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadProgress(0);
    chunksRef.current = [];

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      setAudioBlob(blob);
      setAudioUrl(URL.createObjectURL(blob));
      stream.getTracks().forEach((track) => track.stop());
    };

    mediaRecorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const uploadAudio = async () => {
    setUploadProgress(1);
    const fileName = `storytelling_audio_${uuid()}.webm`;
    const storageRef = bucket.ref(fileName);
    const uploadTask = storageRef.put(audioBlob);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress(progress);
        },
        (err) => reject(err),
        async () => {
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
          resolve(downloadURL);
        }
      );
    });
  };

  const handleProcess = async () => {
    if (!audioBlob) return;
    const downloadURL = await uploadAudio();
    await runPrivacy(downloadURL);
  };

  // The transcript is persisted at the start of save(), which unmounts this
  // tab. So we report the analysis result up to the detail page (which stays
  // mounted) instead of rendering it here.
  const handleSave = async () => {
    try {
      const result = await save();
      // Hand up the full analysis (quotes, anecdotes, TOC couplings, evidence,
      // tags, summary) — the detail page reviews it all as one proposal set.
      onAnalysisComplete?.(result);
    } catch (err) {
      onAnalysisError?.(err.message);
    }
  };

  return (
    <div className="audio-recorder-container">
        {phase === "idle" &&
          (uploadProgress > 0 && uploadProgress < 100 ? (
            <ProgressBar progress={uploadProgress} label="Uploaden" />
          ) : (
            <>
              {!recording && !audioBlob && (
                <button onClick={startRecording}>Start opname</button>
              )}

              {recording && (
                <>
                  <div className="recording-indicator">
                    <div className="recording-indicator-dot" />
                    <p>Opname bezig…</p>
                  </div>
                  <button onClick={stopRecording}>Stop opname</button>
                </>
              )}

              {audioUrl && (
                <div>
                  <audio controls src={audioUrl} />
                </div>
              )}

              {audioBlob && (
                <button onClick={handleProcess}>Verwerken</button>
              )}
            </>
          ))}

        {phase === "processing" && <PrivacyStepsProgress steps={privacySteps} />}

        {phase === "review" && (
          <TranscriptReview transcript={transcript} setTranscript={setTranscript} onSave={handleSave} />
        )}

        {phase === "saving" && (
          <p className="privacy-steps-finalizing">Transcriptie opslaan…</p>
        )}

        {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
};

export default AudioRecorderTab;
